import { UseMutationResult, useMutation } from "@tanstack/react-query";
import elementService from "../../../js/common/ElementService";
import pageMeasureService from "../measure/PageMeasureService";
import luigisboxAnanyticsService from "../luigisbox/luigisboxAnanyticsService";
import ajaxClient from "../../../js/common/AjaxClient";
import { BasketOrderModelResponse, BasketPageCheckoutOrderModel } from "./checkoutOrderView";

const fetchBasketPageData = async (url: string, data?: FetchBasketPageParameters): Promise<BasketPageCheckoutOrderModel> => {
    const response = await ajaxClient.post<BasketOrderModelResponse>(url, data);

    if (response.basketBox !== undefined) {
        elementService.querySelector<HTMLDivElement>("#js-basket-header-container").innerHTML = response.basketBox;
    }

    if (response.dataLayer !== undefined) {
        pageMeasureService.sendDataLayer(response.dataLayer);
    }

    return response.model;
};

const fetchInsertProductToBasket = async (url: string, data: AddProductToBasketParameters): Promise<AddProductToBasketResponse> => {
    const response = await ajaxClient.post<AddProductToBasketResponse>(url, data);

    if (response.basketBox !== undefined) {
        elementService.querySelector<HTMLDivElement>("#js-basket-header-container").innerHTML = response.basketBox;
    }

    if (response.dataLayer !== undefined) {
        pageMeasureService.sendDataLayer(response.dataLayer);
    }

    if (response.product !== undefined) {
        luigisboxAnanyticsService.sendInsertToBasketEvent(response.product.url);
    }

    return response;
};

type FetchBasketPageParameters = Record<string, unknown>;

interface ModifyProductQuantityParameters extends FetchBasketPageParameters {
    readonly productId: number;
    readonly newQuantity: number;
}

export const useModifyProductQuantityMutation = (
    onModelChange: (model: BasketPageCheckoutOrderModel) => void,
): UseMutationResult<BasketPageCheckoutOrderModel, Error, ModifyProductQuantityParameters> => useMutation<BasketPageCheckoutOrderModel, Error, ModifyProductQuantityParameters>({
    mutationFn: (modifyProductQuantityParameters): Promise<BasketPageCheckoutOrderModel> => fetchBasketPageData(
        "/Basket/jsModifyProductQuantityInBasket",
        {
            productId: modifyProductQuantityParameters.productId,
            quantity: modifyProductQuantityParameters.newQuantity,
        },
    ),
    onSuccess: (model) => onModelChange(model),
});

interface ChangeOrderAccessoryParameters extends FetchBasketPageParameters {
    readonly eshopAccessoryId: number;
    readonly isSelected: boolean;
}

export const useChangeOrderAccessoryMutation = (
    onModelChange: (model: BasketPageCheckoutOrderModel) => void,
): UseMutationResult<BasketPageCheckoutOrderModel, Error, ChangeOrderAccessoryParameters> => useMutation<BasketPageCheckoutOrderModel, Error, ChangeOrderAccessoryParameters>({
    mutationFn: (parameters): Promise<BasketPageCheckoutOrderModel> => fetchBasketPageData("/Basket/jsChangeEshopAccessory", parameters),
    onSuccess: (model) => onModelChange(model),
});

interface ChangeOrderAccessoryPriceParameters extends FetchBasketPageParameters {
    readonly eshopAccessoryId: number;
    readonly price: number;
}

export const useChangeOrderAccessoryPriceMutation = (
    onModelChange: (model: BasketPageCheckoutOrderModel) => void,
): UseMutationResult<BasketPageCheckoutOrderModel, Error, ChangeOrderAccessoryPriceParameters> => useMutation({
    mutationFn: (parameters): Promise<BasketPageCheckoutOrderModel> => fetchBasketPageData("/Basket/jsChangeEshopAccessoryPrice", parameters),
    onSuccess: (model) => onModelChange(model),
});

export const useApplyDiscountCodeMutation = (
    discountCode: string,
    onModelChange: (model: BasketPageCheckoutOrderModel) => void,
    setDisountCodeError: (error: string) => void,
): UseMutationResult<BasketPageCheckoutOrderModel, Error, void> => useMutation<BasketPageCheckoutOrderModel>({
    mutationFn: (): Promise<BasketPageCheckoutOrderModel> => fetchBasketPageData(
        "/Basket/jsApplyDiscount",
        { code: discountCode },
    ),
    onSuccess: (model) => onModelChange(model),
    onError: (error) => {
        setDisountCodeError(error.message);
    },
});

export const useRemoveDiscountCodeMutation = (
    onModelChange: (model: BasketPageCheckoutOrderModel) => void,
): UseMutationResult<BasketPageCheckoutOrderModel, Error, void> => useMutation<BasketPageCheckoutOrderModel>({
    mutationFn: (): Promise<BasketPageCheckoutOrderModel> => fetchBasketPageData("/Basket/jsRemoveDiscountCode"),
    onSuccess: (model) => onModelChange(model),
});

interface AddProductToBasketParameters {
    readonly productId: number;
    readonly insertPageUrl: string;
}

export interface InsertedProductToBasket {
    id: number;
    imageUrl: string;
    title: string;
    isMultivariant: boolean;
    url: string;
}

export type AddProductToBasketResult = "success" | "already_in_basket" | "sold_out" | "discount_code_already_used" | "too_many_package_of_week";

export interface AddProductToBasketResponse {
    result: AddProductToBasketResult;
    product?: InsertedProductToBasket;
    maximalQuantity?: number;
    basketPageUrl: string;
    basketBox?: string;
    dataLayer?: Record<string, unknown>[];
}

export const insertProductToBasket = (productId: number): Promise<AddProductToBasketResponse> => fetchInsertProductToBasket("/Basket/jsAddProductToBasket", {
    productId,
    insertPageUrl: globalThis.location.href,
});
